import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useI18n } from "@/lib/i18n";

/**
 * RafflePoster — A5 print-ready poster.
 * Public route, no auth. Displays a big QR to https://danamark.dk/raffle#buy
 * alongside a short trilingual call-to-action, bundle prices and the KB Hallen
 * draw date. A "Print" button opens the browser dialog with A5 print CSS.
 *
 * Print flow (A5 portrait, 148 × 210 mm):
 *   1. Open /raffle-poster
 *   2. Choose language (DA / EN / ES) with the top buttons
 *   3. Click "Print" → in the dialog choose paper size A5 or "fit to page" on A4.
 */
const QR_URL = "https://danamark.dk/raffle#buy";

const COPY = {
  da: {
    kicker: "DANAMARK-LODTRÆKNING",
    title: "SCAN & KØB LODDER",
    subtitle: "Vind fantastiske præmier. Støt en god sag.",
    scanLine: "Scan koden med din telefon",
    fromPrice: "Fra kun 50 kr.",
    bundlesTitle: "Priser",
    bundles: ["1 lod — 50 kr.", "2 lodder — 100 kr.", "3 lodder — 150 kr.", "5 lodder — 225 kr.  · SPAR 25 kr.", "10 lodder — 400 kr.  · SPAR 100 kr."],
    drawDate: "Lodtrækning 25. oktober 2026 · KB Hallen",
    proceeds: "Hele overskuddet støtter de områder i Spanien, der er ramt af skovbrandene.",
    printBtn: "Udskriv A5",
    downloadBtn: "Download PDF",
    urlLabel: "Eller besøg",
  },
  en: {
    kicker: "DANAMARK RAFFLE",
    title: "SCAN & BUY TICKETS",
    subtitle: "Win amazing prizes. Support a good cause.",
    scanLine: "Scan with your phone",
    fromPrice: "From only 50 DKK",
    bundlesTitle: "Bundles",
    bundles: ["1 ticket — 50 DKK", "2 tickets — 100 DKK", "3 tickets — 150 DKK", "5 tickets — 225 DKK  · SAVE 25 DKK", "10 tickets — 400 DKK  · SAVE 100 DKK"],
    drawDate: "Raffle draw · 25 October 2026 · KB Hallen",
    proceeds: "All proceeds support the areas of Spain affected by the wildfires.",
    printBtn: "Print A5",
    downloadBtn: "Download PDF",
    urlLabel: "Or visit",
  },
  es: {
    kicker: "RIFA DE DANAMARK",
    title: "ESCANEA Y COMPRA BOLETOS",
    subtitle: "Gana premios increíbles. Apoya una buena causa.",
    scanLine: "Escanea con tu móvil",
    fromPrice: "Desde solo 50 DKK",
    bundlesTitle: "Packs",
    bundles: ["1 boleto — 50 DKK", "2 boletos — 100 DKK", "3 boletos — 150 DKK", "5 boletos — 225 DKK  · AHORRA 25 DKK", "10 boletos — 400 DKK  · AHORRA 100 DKK"],
    drawDate: "Sorteo · 25 de octubre de 2026 · KB Hallen",
    proceeds: "Toda la recaudación apoya a las zonas de España afectadas por los incendios.",
    printBtn: "Imprimir A5",
    downloadBtn: "Descargar PDF",
    urlLabel: "O visita",
  },
};

export default function RafflePoster() {
  const { lang: appLang } = useI18n();
  const [lang, setLang] = useState(appLang || "da");
  const c = COPY[lang] || COPY.da;

  return (
    <div className="min-h-screen bg-line/20 py-6">
      {/* ── Screen-only toolbar (never prints) ─────────────────── */}
      <div className="no-print max-w-[560px] mx-auto flex flex-wrap items-center justify-between gap-3 px-6 mb-4">
        <div className="flex gap-1.5" data-testid="poster-lang-switch">
          {["da", "en", "es"].map(l => (
            <button key={l} onClick={() => setLang(l)}
                    data-testid={`poster-lang-${l}`}
                    className={`px-3 py-1.5 text-xs tracking-widest rounded-full border transition-colors uppercase ${
                      lang === l ? "border-terracotta text-terracotta bg-white" : "border-line text-ink/60 hover:border-ink"
                    }`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/raffle/poster.pdf?lang=${lang}`}
             download={`danamark-raffle-poster-${lang}.pdf`}
             data-testid="poster-download-pdf"
             className="border border-ink text-ink hover:bg-ink hover:text-white transition-colors px-4 py-1.5 text-xs tracking-widest rounded-full uppercase">
            {c.downloadBtn}
          </a>
          <button onClick={() => window.print()}
                  data-testid="poster-print"
                  className="bg-terracotta hover:bg-terracotta-dark text-white px-4 py-1.5 text-xs tracking-widest rounded-full uppercase">
            {c.printBtn}
          </button>
        </div>
      </div>

      {/* ── A5 poster canvas ───────────────────────────────────── */}
      <article
        data-testid="raffle-poster"
        className="poster mx-auto bg-white shadow-lg text-ink relative overflow-hidden"
      >
        <div className="poster-inner">
          {/* Top bar */}
          <div className="flex items-center justify-between border-b border-ink/20 pb-3">
            <div className="flex items-center gap-2.5">
              <img src="/logo.svg" alt="DANAmark" className="h-8 w-8 rounded-full" onError={(e) => { e.target.style.display = "none"; }} />
              <div className="leading-tight">
                <div className="font-editorial text-sm tracking-tight">DANAmark</div>
                <div className="label-mono text-[8px] text-ink/60 tracking-widest">TOGETHER ACROSS BORDERS</div>
              </div>
            </div>
            <div className="label-mono text-[9px] tracking-widest text-terracotta">{c.kicker}</div>
          </div>

          {/* Headline */}
          <h1 className="mt-5 font-editorial text-[38px] leading-[0.98] tracking-tighter" data-testid="poster-title">
            {c.title}
          </h1>
          <p className="mt-2 text-ink/75 text-[13px] leading-snug italic">{c.subtitle}</p>

          {/* QR block */}
          <div className="mt-5 flex items-center gap-5">
            <div className="border border-ink/20 rounded-lg p-2 bg-white shrink-0" data-testid="poster-qr">
              <QRCodeSVG value={QR_URL} size={168} level="H" includeMargin={false} bgColor="#ffffff" fgColor="#111111" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-mono text-[9px] text-ink/60 tracking-widest">→ SCAN</div>
              <div className="mt-1 font-editorial text-lg leading-tight">{c.scanLine}</div>
              <div className="mt-3 inline-block bg-terracotta text-white font-editorial text-base px-3 py-1 rounded-full">
                {c.fromPrice}
              </div>
              <div className="mt-3 text-[10px] text-ink/60 leading-snug">
                {c.urlLabel}<br/><span className="font-mono text-ink text-[11px]">danamark.dk/raffle#buy</span>
              </div>
            </div>
          </div>

          {/* Bundles */}
          <div className="mt-5 border border-line rounded-lg p-3">
            <div className="label-mono text-[9px] tracking-widest text-terracotta">{c.bundlesTitle}</div>
            <ul className="mt-2 space-y-1 text-[12px] leading-snug text-ink/85">
              {c.bundles.map((b, i) => <li key={i} className="tabular-nums">• {b}</li>)}
            </ul>
          </div>

          {/* Draw + proceeds */}
          <div className="mt-4 border-t border-ink/20 pt-3 flex flex-col gap-1.5">
            <div className="font-editorial text-[13px] text-terracotta leading-snug">{c.drawDate}</div>
            <div className="text-[10px] text-ink/70 leading-snug">{c.proceeds}</div>
          </div>

          <div className="absolute bottom-3 left-6 right-6 flex items-baseline justify-between label-mono text-[8px] text-ink/40 tracking-widest">
            <span>CVR 45188981</span>
            <span>danamarksolidaritet@gmail.com</span>
          </div>
        </div>
      </article>

      {/* Print + poster styles */}
      <style>{`
        .poster {
          /* A5 portrait: 148mm × 210mm */
          width: 148mm;
          height: 210mm;
        }
        .poster-inner {
          width: 100%;
          height: 100%;
          padding: 14mm 12mm 16mm 12mm;
          position: relative;
        }
        @media print {
          @page { size: A5 portrait; margin: 0; }
          html, body { background: #fff !important; }
          .no-print { display: none !important; }
          .poster {
            box-shadow: none !important;
            width: 148mm !important;
            height: 210mm !important;
            margin: 0 !important;
            page-break-after: avoid;
          }
        }
      `}</style>
    </div>
  );
}
